import { z } from 'zod';
import { isRealDate, paginationQuerySchema, uuidSchema } from './common.js';
const text = (max) => z.string().trim().max(max);
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine(isRealDate, 'Invalid calendar date');
const clockTime = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM')
    .transform((v) => v);
const atLeastOne = (v) => Object.keys(v).length > 0;
const MS_PER_DAY = 86_400_000;
const daysBetween = (from, to) => (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY;
export const MAX_REPORT_DAYS = 366;
// -------------------------------------------------------------- attendance
export const ATTENDANCE_STATUSES = ['present', 'absent', 'late', 'excused', 'half_day'];
export const attendanceDateQuerySchema = z.object({ date: isoDate });
export const attendanceRecordSchema = z.object({
    studentId: uuidSchema,
    status: z.enum(ATTENDANCE_STATUSES),
    remarks: text(500)
        .transform((v) => (v === '' ? null : v))
        .nullable()
        .optional(),
});
export const markAttendanceSchema = z
    .object({
    records: z.array(attendanceRecordSchema).min(1).max(300),
    /** Required when the register was already submitted and a mark changes. */
    reason: text(300).optional(),
})
    .refine((v) => new Set(v.records.map((r) => r.studentId)).size === v.records.length, {
    message: 'A student can appear only once',
    path: ['records'],
});
export const attendanceSummaryQuerySchema = z.object({ date: isoDate.optional() });
const rangeRefinement = (v) => v.to >= v.from && daysBetween(v.from, v.to) < MAX_REPORT_DAYS;
const rangeMessage = {
    message: `Range must be ordered and at most ${MAX_REPORT_DAYS} days`,
    path: ['to'],
};
export const studentAttendanceQuerySchema = z
    .object({ from: isoDate, to: isoDate })
    .refine(rangeRefinement, rangeMessage);
export const attendanceReportQuerySchema = paginationQuerySchema
    .extend({
    from: isoDate,
    to: isoDate,
    sectionId: uuidSchema.optional(),
    classId: uuidSchema.optional(),
})
    .refine(rangeRefinement, rangeMessage);
// ---------------------------------------------------------------- timetable
export const createPeriodSchema = z
    .object({
    name: text(50).min(1),
    startTime: clockTime,
    endTime: clockTime,
    isBreak: z.boolean().default(false),
})
    .refine((v) => v.endTime > v.startTime, {
    message: 'End time must be after start time',
    path: ['endTime'],
});
export const updatePeriodSchema = z
    .object({
    name: text(50).min(1),
    startTime: clockTime,
    endTime: clockTime,
    isBreak: z.boolean(),
})
    .partial()
    .refine(atLeastOne, 'At least one field is required')
    .refine((v) => !v.startTime || !v.endTime || v.endTime > v.startTime, {
    message: 'End time must be after start time',
    path: ['endTime'],
});
const roomSchema = text(50)
    .transform((v) => (v === '' ? null : v))
    .nullable();
export const createTimetableEntrySchema = z.object({
    academicYearId: uuidSchema.optional(),
    sectionId: uuidSchema,
    periodId: uuidSchema,
    dayOfWeek: z.number().int().min(0).max(6),
    subjectId: uuidSchema,
    staffId: uuidSchema,
    room: roomSchema.optional(),
});
export const updateTimetableEntrySchema = z
    .object({
    periodId: uuidSchema,
    dayOfWeek: z.number().int().min(0).max(6),
    subjectId: uuidSchema,
    staffId: uuidSchema,
    room: roomSchema,
})
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const listTimetableQuerySchema = z
    .object({
    academicYearId: uuidSchema.optional(),
    sectionId: uuidSchema.optional(),
    staffId: uuidSchema.optional(),
})
    .refine((v) => !!v.sectionId || !!v.staffId, {
    message: 'Provide sectionId or staffId',
    path: ['sectionId'],
});
// -------------------------------------------------------------------- leave
export const LEAVE_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'];
export const createLeaveRequestSchema = z
    .object({ fromDate: isoDate, toDate: isoDate, reason: text(500).min(1) })
    .refine((v) => v.toDate >= v.fromDate, {
    message: 'End date cannot be before start date',
    path: ['toDate'],
})
    .refine((v) => daysBetween(v.fromDate, v.toDate) <= 90, {
    message: 'A leave request can span at most 91 days',
    path: ['toDate'],
});
export const reviewLeaveRequestSchema = z.object({
    decision: z.enum(['approved', 'rejected']),
    note: text(500)
        .transform((v) => (v === '' ? null : v))
        .nullable()
        .optional(),
});
export const listLeaveRequestsQuerySchema = paginationQuerySchema.extend({
    status: z.enum(LEAVE_STATUSES).optional(),
    studentId: uuidSchema.optional(),
});
//# sourceMappingURL=attendance.js.map